import React, { useState, useEffect } from 'react';
// Common Components
import AppPaginator from '../../AppPaginator';
import AppSearchBox from '../../AppSearchBox';
import AppBreadCrumb from '../../common/AppBreadCrumb';
import PerPageBlock from '../../common/PerPageBlock';
import AppToaster from '../../AppToaster';
// API & Utils
import { getSalesInvoices, fetchSalesInvoice } from '../../../api/SalesInvoicesAPI';
import { handleApiError } from '../../../api/risapi';
// Hooks & Helpers
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { format, parseISO } from 'date-fns';

const SalesInvoicesPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const successMessage = location.state?.message;

    const [entityName, setEntityName] = useState('Sales Invoice');
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [reportLoading, setReportLoading] = useState(false);
    const [viewDataLoading, setViewDataLoading] = useState(false);
    const [searchTermCompleted, setSearchTermCompleted] = useState('');
    const [perPage, setPerPage] = useState(process.env.REACT_APP_PER_PAGE_COUNT || 10);

    const [invoicesList, setInvoicesList] = useState({
        paginate: true, data: [], per_page: perPage, current_page: 1, tot_page: null,
    });

    const [toaster, setToaster] = useState({});
    const [showToaster, setShowToaster] = useState(false);
    const [saveCompleted, setSaveCompleted] = useState(false); 

    // --- Navigation ---
    const handleAddClick = () => navigate('/franchisee_user/sales/sales-invoices/create');
    
    // Separate explicit View and Edit routes
    const handleViewClick = (id) => navigate(`/franchisee_user/sales/sales-invoices/view/${id}`);
    const handleEditClick = (id) => navigate(`/franchisee_user/sales/sales-invoices/edit/${id}`);

    // --- Delete Logic ---
    const handleDeleteClick = (id) => {
        Swal.fire(
            'Action Not Allowed',
            'Posted Sales Invoices cannot be deleted. Please issue a Sales Return / Credit Note to reverse this transaction.',
            'warning'
          );
    };

    // --- Effects ---
    useEffect(() => {
         if (successMessage) {
            setShowToaster(true);
            setToaster({ type: 'success', heading: 'Success', message: successMessage });
            navigate(location.pathname, { replace: true, state: {} });
         }
     }, [successMessage, location.pathname, navigate]);

    useEffect(() => {
         document.title = 'Manage Sales Invoices';
         let filters = { paginate: true, page: currentPage, per_page: perPage, search_term: searchTermCompleted };
         setViewDataLoading(true);

         getSalesInvoices(filters, function (response) {
             setViewDataLoading(false);
             if (response.sales_invoices) { 
                 let data = []; let currentP = 1; let lastP = 1; let responsePP = perPage;
                 if (Array.isArray(response.sales_invoices)) { data = response.sales_invoices; }
                 else if (response.sales_invoices.data && Array.isArray(response.sales_invoices.data)) {
                     data = response.sales_invoices.data; currentP = response.sales_invoices.current_page; lastP = response.sales_invoices.last_page; responsePP = response.sales_invoices.per_page;
                 } else { console.error("API structure error:", response.sales_invoices); handleApiError(response); return; }
                 setInvoicesList({ paginate: filters.paginate, per_page: responsePP, data: data, current_page: currentP, tot_page: lastP });
             } else {
                  handleApiError(response); setToaster({ type: 'danger', heading: 'Error', message: 'Could not load Sales Invoices.' }); setShowToaster(true);
                  setInvoicesList(prev => ({ ...prev, data: [], current_page: 1, tot_page: 1 }));
             }
         });
     }, [currentPage, searchTermCompleted, saveCompleted, perPage]);

    // Helper to format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try { return format(parseISO(dateString), 'dd-MMM-yyyy'); }
        catch (error) { return dateString; }
    };

    return (
        <>
            <AppToaster showToaster={showToaster} setShowToaster={setShowToaster} toaster={toaster} />
            <main>
                <div className="container-fluid">
                    <AppBreadCrumb title='Sales Invoices List'>
                        <li><a className="f-s-14 f-w-500" href="#">Sales</a></li>
                        <li className="active"><a className="f-s-14 f-w-500" href="#">Sales Invoices</a></li>
                    </AppBreadCrumb>

                    <div className="row bg-white">
                        <div className="col-12">
                            <div className="tab-wrapper mb-3">
                                <ul className="tabs">
                                    <li className="tab-link active" data-tab={1}>All Sales Invoices</li>
                                    <li className="ms-md-auto">
                                        <div className="text-md-end d-flex flex-column flex-md-row mt-md-3 ">
                                            <AppSearchBox searchTerm={searchTerm} setSearchTerm={setSearchTerm} setSearchTermCompleted={setSearchTermCompleted} />
                                            <button className="text-nowrap btn btn-red btn-sm  fw-semibold f-s-14 ms-md-3" type="button" onClick={handleAddClick}>
                                                <i className="ph-bold  ph-circles-three-plus"></i> Add Sales Invoice
                                            </button>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                            <div className="content-wrapper" id="card-container">
                                <div className="d-flex flex-wrap m-md-3">
                                    {invoicesList.data.length > 0 ? (
                                        <>
                                            <AppPaginator entityName={entityName} currentPage={invoicesList.current_page} totalPages={invoicesList.tot_page} setCurrentPage={setCurrentPage} />
                                            <PerPageBlock perPage={perPage} setPerPage={setPerPage} />
                                        </>
                                    ) : ( !viewDataLoading && <p className="text-center w-100">No Sales Invoices found.</p> )}
                                    {(reportLoading || viewDataLoading) && <div className="loader_box ms-auto"><div className="loader_14"></div></div>}
                                </div>

                                <div className="tabs-content active" id="tab-1">
                                    <div className="row">
                                        {!viewDataLoading && invoicesList.data.map((invoice) => (
                                            <div className="col-md-6 col-xl-4 project-card" key={invoice.id}>
                                                <div className="card hover-effect">
                                                    <div className="card-header">
                                                        <div className="d-flex align-items-center">
                                                            <div className="h-40 w-40 d-flex-center b-r-50 overflow-hidden me-2 btn-light-reddit">
                                                                 <i className="ti ti-receipt-2 f-s-18"></i>
                                                            </div>
                                                            <div className="flex-grow-1 ps-2">
                                                                <h6 className="m-0 text-dark f-w-600">{invoice.party?.name || 'N/A'}</h6>
                                                                <small className="text-muted d-block">Inv#: {invoice.invoice_number || '-'} | {formatDate(invoice.invoice_date)}</small>
                                                            </div>
                                                            <div className="dropdown">
                                                                <button aria-expanded="false" className="bg-none border-0" data-bs-toggle="dropdown" type="button"><i className="ti ti-dots-vertical text-dark" /></button>
                                                                <ul className="dropdown-menu dropdown-menu-end">
                                                                    
                                                                    {/* 1. View Button */}
                                                                    <li><a className="dropdown-item" onClick={() => handleViewClick(invoice.id)}><i className="ti ti-eye text-info me-1" /> View</a></li>
                                                                    
                                                                    {/* 2. Edit Button (Conditionally Locked) */}
                                                                    <li>
                                                                        {invoice.is_editable !== false ? (
                                                                            <a className="dropdown-item" onClick={() => handleEditClick(invoice.id)}><i className="ti ti-edit text-primary me-1" /> Edit</a>
                                                                        ) : (
                                                                            <span className="dropdown-item text-muted" title="Paid via non-cash method. Cannot edit.">
                                                                                <i className="ti ti-edit me-1" /> Edit <small>(Locked)</small>
                                                                            </span>
                                                                        )}
                                                                    </li>
                                                                    
                                                                    <li><button className="dropdown-item delete-button" onClick={() => handleDeleteClick(invoice.id)}><i className="ti ti-trash text-danger me-1" /> Delete</button></li>
                                                                    <li><hr className="dropdown-divider"/></li>
                                                                    <li><a className="dropdown-item" onClick={() => navigate(`/franchisee_user/sales/sales-returns/create?source_invoice_id=${invoice.id}`)}><i className="ti ti-receipt-refund text-warning me-1" /> Create Return/Credit Note</a></li>
                                                                </ul>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="card-body py-2">
                                                         <div className="d-flex justify-content-between align-items-center">
                                                             <div>
                                                                  <p className="f-w-500 text-secondary mb-0">Total Amount</p>
                                                                  <h6 className="f-w-600 mb-0">₹{parseFloat(invoice.total_amount || 0).toFixed(2)}</h6>
                                                             </div>
                                                         </div>
                                                     </div>
                                                </div>
                                            </div>
                                        ))}
                                        {!viewDataLoading && invoicesList.data.length === 0 && (
                                            <div className="col-12"> <div className="card mb-3"><div className="row g-0"><div className="col-md-12"><div className="card-body"><h5 className="card-title">No Sales Invoices Found</h5><p className="card-text">No records created yet.</p></div></div></div></div></div>
                                        )}
                                    </div>
                                </div>

                               
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
};

export default SalesInvoicesPage;